import heic2any from "heic2any";

const HEIC_MIME_TYPES = new Set([
  "image/heic",
  "image/heif",
  "image/heifs",
  "image/heic-sequence",
  "image/heif-sequence",
]);

const HEIC_EXTENSIONS = new Set(["heic", "heif"]);

export const isHeicFile = (file) => {
  if (!file) return false;

  const ext = file.name?.split(".").pop()?.toLowerCase();
  return HEIC_MIME_TYPES.has(file.type) || (ext && HEIC_EXTENSIONS.has(ext));
};

const blobFromResult = (result) => {
  if (result instanceof Blob) {
    return result;
  }

  if (Array.isArray(result)) {
    return new Blob(result, { type: "image/jpeg" });
  }

  return new Blob([result], { type: "image/jpeg" });
};

export const getPreviewBlob = async (file) => {
  if (!file) return null;

  if (!isHeicFile(file)) {
    return file;
  }

  try {
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.92,
    });

    return blobFromResult(converted);
  } catch (error) {
    console.error("HEIC conversion failed, falling back to original file", error);
    return file;
  }
};

export const getPreviewDataUrl = async (file) => {
  const blob = await getPreviewBlob(file);
  if (!blob) return null;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const getPreviewObjectUrl = async (file) => {
  const blob = await getPreviewBlob(file);
  if (!blob) return null;

  return URL.createObjectURL(blob);
};

// Helper to check if a URL points to a HEIC file
const isHeicUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase();
  return lower.endsWith(".heic") || lower.endsWith(".heif") || lower.includes(".heic?") || lower.includes(".heif?");
};

// Convert a remote HEIC image URL to a JPEG preview URL
export const getPreviewUrlFromRemote = async (imageUrl) => {
  if (!imageUrl) return null;

  // If it's not a HEIC URL, return as-is
  if (!isHeicUrl(imageUrl)) {
    return imageUrl;
  }

  try {
    // Fetch the image with explicit headers
    // Note: Browsers may automatically send Range headers for large files
    // We'll handle 206 Partial Content responses if they occur
    const response = await fetch(imageUrl, {
      method: 'GET',
      mode: 'cors',
      cache: 'default',
      headers: {
        'Accept': 'image/heic,image/heif,image/*,*/*',
      },
    });
    
    if (!response.ok) {
      console.error("Failed to fetch image:", response.status, response.statusText);
      return imageUrl; // Fallback to original URL
    }

    // Check if we got a partial content response (206)
    // If so, we need to handle it differently
    if (response.status === 206) {
      console.warn("Received partial content (206) for HEIC file, attempting full fetch");
      // Try fetching again without any Range headers
      const fullResponse = await fetch(imageUrl, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache', // Force fresh request
        headers: {
          'Accept': 'image/heic,image/heif,image/*,*/*',
        },
      });
      
      if (!fullResponse.ok || fullResponse.status === 206) {
        console.error("Still receiving partial content, may need to handle chunked download");
        // Fallback: try to use the partial response if it's the only option
        const partialBlob = await fullResponse.blob();
        if (partialBlob && partialBlob.size > 0) {
          // Check if it's actually a HEIC file
          const isHeic = HEIC_MIME_TYPES.has(partialBlob.type) || isHeicUrl(imageUrl);
          if (isHeic) {
            try {
              const converted = await heic2any({
                blob: partialBlob,
                toType: "image/jpeg",
                quality: 0.92,
              });
              const jpegBlob = blobFromResult(converted);
              return URL.createObjectURL(jpegBlob);
            } catch (convError) {
              console.error("Failed to convert partial HEIC:", convError);
            }
          }
        }
        return imageUrl; // Fallback to original URL
      }
      
      // Use the full response
      const arrayBuffer = await fullResponse.arrayBuffer();
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        console.error("Received empty arrayBuffer");
        return imageUrl;
      }
      
      const contentType = fullResponse.headers.get('content-type') || 'image/heic';
      const blob = new Blob([arrayBuffer], { type: contentType });
      
      // Check if it's actually a HEIC file
      const isHeic = HEIC_MIME_TYPES.has(blob.type) || isHeicUrl(imageUrl);
      if (!isHeic) {
        return imageUrl;
      }

      // Convert HEIC to JPEG
      const converted = await heic2any({
        blob: blob,
        toType: "image/jpeg",
        quality: 0.92,
      });

      const jpegBlob = blobFromResult(converted);
      return URL.createObjectURL(jpegBlob);
    }

    // Normal 200 response - use arrayBuffer for more reliable binary data handling
    const arrayBuffer = await response.arrayBuffer();
    
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      console.error("Received empty arrayBuffer");
      return imageUrl;
    }
    
    // Convert arrayBuffer to blob
    const contentType = response.headers.get('content-type') || 'image/heic';
    const blob = new Blob([arrayBuffer], { type: contentType });
    
    // Verify blob is not empty
    if (!blob || blob.size === 0) {
      console.error("Created empty blob from arrayBuffer");
      return imageUrl;
    }
    
    // Check if it's actually a HEIC file (by MIME type or extension)
    const isHeic = HEIC_MIME_TYPES.has(blob.type) || isHeicUrl(imageUrl);
    
    if (!isHeic) {
      // Not HEIC, return original URL
      return imageUrl;
    }

    // Convert HEIC to JPEG
    const converted = await heic2any({
      blob: blob,
      toType: "image/jpeg",
      quality: 0.92,
    });

    const jpegBlob = blobFromResult(converted);
    return URL.createObjectURL(jpegBlob);
  } catch (error) {
    console.error("Failed to convert remote HEIC image, using original URL:", error);
    // If it's a network error, try to provide more details
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.error("Network error - possible CORS issue or network failure");
    } else if (error.message && error.message.includes('incomplete')) {
      console.error("Incomplete content error - possible Range request issue");
    }
    return imageUrl; // Fallback to original URL
  }
};

